'use client';

import { useTranslations } from 'next-intl';

type SeatSelectorProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  unitPrice: string;
  totalPrice: string;
};

export const SeatSelector = ({ value, onChange, min = 1, max, unitPrice, totalPrice }: SeatSelectorProps) => {
  const t = useTranslations('billing.seats');

  const clamp = (next: number) => {
    const upperBounded = max ? Math.min(max, next) : next;
    return Math.max(min, upperBounded);
  };

  return (
    <div className="rounded-lg border border-base-300 bg-base-100 p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">{t('title')}</p>
          <p className="text-xs text-[var(--color-fg-muted)]">{t('description')}</p>
        </div>
        <div className="join">
          <button
            type="button"
            className="btn join-item btn-sm"
            aria-label={t('decrement')}
            disabled={value <= min}
            onClick={() => onChange(clamp(value - 1))}
          >
            -
          </button>
          <input
            type="number"
            className="input join-item input-bordered input-sm w-20 text-center"
            min={min}
            max={max}
            value={value}
            onChange={(event) => onChange(clamp(Number(event.target.value) || min))}
          />
          <button
            type="button"
            className="btn join-item btn-sm"
            aria-label={t('increment')}
            disabled={max ? value >= max : false}
            onClick={() => onChange(clamp(value + 1))}
          >
            +
          </button>
        </div>
      </div>
      <div className="mt-3 grid gap-2 text-xs text-[var(--color-fg-muted)] sm:grid-cols-2">
        <div className="rounded-md bg-base-200/70 px-3 py-2">
          <span className="block text-[var(--color-fg-subtle)]">{t('unitPrice')}</span>
          <span className="font-semibold">{unitPrice}</span>
        </div>
        <div className="rounded-md bg-primary/10 px-3 py-2 text-primary">
          <span className="block opacity-75">{t('totalPrice')}</span>
          <span className="font-semibold">{totalPrice}</span>
        </div>
      </div>
    </div>
  );
};
